import React, { useState, useEffect, useMemo } from "react";
import "./style.css";
import { ConfigProvider, message, Modal } from "antd";
import ptBR from "antd/lib/locale/pt_BR";
import dayjs from "dayjs";
import 'dayjs/locale/pt-br';
import { InboxOutlined } from "@ant-design/icons";

import MovimentacaoFinanceiraHeader from "./components/MovimentacaoFinanceiraHeader";
import MovimentacaoFinanceiraStats from "./components/MovimentacaoFinanceiraStats";
import MovimentacaoFinanceiraTable from "./components/MovimentacaoFinanceiraTable";
import BatchActionsBar from "./components/BatchActionsBar";
import LancamentoModal from "./modals/LancamentoModal";
import ImportarArquivoModal from "./modals/ImportarArquivoModal";
import InstrucoesModal from "./modals/InstrucoesModal";
import ReclassificacaoModal from "./modals/ReclassificacaoModal";
import ContaBancariaModal from "./modals/ContaBancariaModal";
import GerenciarContasModal from "./modals/GerenciarContasModal";
import DashboardCustosModal from "./modals/DashboardCustosModal";
import WhatsNewModal from "./modals/WhatsNewModal";
import OpenFinanceModal from "./modals/OpenFinanceModal";
import OpenFinanceAvisoModal from "./modals/OpenFinanceAvisoModal";
import RegrasClassificacaoModal from "./modals/RegrasClassificacaoModal";
import { Method_POST_CONTEXTO, Method_GET_CONTEXTO_PDF } from "../../../../service/api";
import { motion, AnimatePresence } from "framer-motion";


import { useNavigate } from "react-router-dom";
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { soDigitos, agruparContasPorDV, contaBateNoGrupo } from './utils/contas';


export function MovimentacaoFinanceira() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [period, setPeriod] = useState([dayjs().startOf("month"), dayjs().endOf("month")]);
    const [bankAccount, setBankAccount] = useState('');
    const [data, setData] = useState([]);
    const [filterSituacao, setFilterSituacao] = useState('');
    const [filterTipo, setFilterTipo] = useState('');
    const [filterCategoria, setFilterCategoria] = useState('');
    const [filterTexto, setFilterTexto] = useState('');
    const [availableCategorias, setAvailableCategorias] = useState([]);
    const [rawData, setRawData] = useState({ lancamentos: [], saldoAnterior: 0 });
    const [stats, setStats] = useState({
        saldoAnterior: 15450.50,
        creditosMes: 12300.00,
        debitosMes: 8500.25,
        saldoMes: 19250.25
    });
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);

    const [modalConfig, setModalConfig] = useState({
        open: false,
        mode: 'new',
        type: 'D',
        data: null
    });

    const [bankAccounts, setBankAccounts] = useState([]);
    // Só pro SELETOR e pro filtro: unifica a mesma conta duplicada por causa do
    // dígito verificador (OFX × Open Finance). O `bankAccounts` original segue
    // intacto pro modal/tabela/export, que dependem dos ids reais.
    const contasAgrupadas = useMemo(() => agruparContasPorDV(bankAccounts), [bankAccounts]);
    const [userProfile, setUserProfile] = useState(null);
    const [isContador, setIsContador] = useState(false);
    const [filterPendencias, setFilterPendencias] = useState(false);
    const [hasPendencias, setHasPendencias] = useState(false);
    const [instrucoesModalOpen, setInstrucoesModalOpen] = useState(false);
    const [reclassificacaoModalOpen, setReclassificacaoModalOpen] = useState(false);
    const [contaBancariaModalOpen, setContaBancariaModalOpen] = useState(false);
    const [gerenciarContasModalOpen, setGerenciarContasModalOpen] = useState(false);
    const [dashboardModalOpen, setDashboardModalOpen] = useState(false);
    const [whatsNewModalOpen, setWhatsNewModalOpen] = useState(false);
    const [openFinanceModalOpen, setOpenFinanceModalOpen] = useState(false);
    const [openFinanceAvisoOpen, setOpenFinanceAvisoOpen] = useState(false);
    const [rulesModalOpen, setRulesModalOpen] = useState(false);
    const [globalPendencies, setGlobalPendencies] = useState([]);
    const [editingAccount, setEditingAccount] = useState(null);

    const [whatsNewBadge, setWhatsNewBadge] = useState(false);

    useEffect(() => {
        const userSession = sessionStorage.getItem("USER");
        if (userSession) {
            try {
                const userData = JSON.parse(userSession);
                const perfis = Array.isArray(userData) ? userData : (userData.perfis || []);

                const contador = perfis.some(p =>
                    p.id === "GRP_PORTAL_CONTADOR" ||
                    p.id === "GRP_PORTAL_ADMIN" ||
                    p.id === "GRP_ADMIN"
                )

                setIsContador(contador);
                setUserProfile(perfis);
            } catch (e) {
                console.error("Erro ao ler perfis do sessionStorage", e);
            }
        }

        // Verifica se é o primeiro acesso à nova versão
        const hasSeenWhatsNew = localStorage.getItem("HAS_SEEN_FINANCE_V2");
        if (!hasSeenWhatsNew) {
            setWhatsNewBadge(true);
        }

        // Aviso do Open Finance (nova captura mensal): abre automático 1x.
        const hasSeenOfAviso = localStorage.getItem("HAS_SEEN_OPEN_FINANCE_AVISO");
        if (!hasSeenOfAviso) {
            setOpenFinanceAvisoOpen(true);
            localStorage.setItem("HAS_SEEN_OPEN_FINANCE_AVISO", "1");
        }

        fetchAccounts();
        fetchGlobalPendencies();
    }, []);

    const handleOpenWhatsNew = () => {
        setWhatsNewModalOpen(true);
        setWhatsNewBadge(false);
    };

    const fetchGlobalPendencies = async () => {
        try {
            // Busca pendências de até 6 meses atrás (exceto o mês atual que já é filtrado)
            const sixMonthsAgo = dayjs().subtract(6, 'month').startOf('month').toISOString();
            const lastMonthEnd = dayjs().subtract(1, 'month').endOf('month').toISOString();

            const payload = {
                dataInicio: sixMonthsAgo,
                dataTermino: lastMonthEnd,
                situacaoIds: ['W', 'N']
            };

            const response = await Method_POST_CONTEXTO("/contabil/lancamento/pesquisar", payload);
            setGlobalPendencies(response?.lancamentos || []);
        } catch (error) {
            console.error("Erro ao buscar pendências globais:", error);
        }
    };

    const fetchAccounts = async () => {
        try {
            const res = await Method_POST_CONTEXTO("/cliente/conta-bancaria/listar");
            setBankAccounts(Array.isArray(res) ? res : []);
        } catch (error) {
            console.error("Erro ao carregar contas:", error);
        }
    };

    // Rebusca ao trocar período OU conta — o filtro de conta é feito no BACKEND
    // (que filtra lançamentos E o saldo pela conta; inclusive contas "só banco"
    // sem número, que o filtro client-side não casava, e o saldo passa a seguir
    // a conta selecionada).
    useEffect(() => {
        handleSearch();
    }, [period, bankAccount]);

    useEffect(() => {
        applyFilters();
    }, [rawData, filterPendencias, filterSituacao, filterTipo, filterCategoria, filterTexto]);

    const handleSearch = async () => {
        setLoading(true);
        try {
            // O filtro por conta vai pro BACKEND: id 0 = Caixa; id > 0 = conta
            // específica; vazio = todas. Assim os lançamentos E o saldo já vêm
            // filtrados pela conta (o Pantheon casa "só banco" por banco_codigo).
            const payload = {
                dataInicio: period[0].startOf("day").toISOString(),
                dataTermino: period[1].endOf("day").toISOString(),
            };
            if (bankAccount !== '' && bankAccount !== undefined && bankAccount !== null) {
                payload.lancamento = { contaBancaria: { id: Number(bankAccount) } };
            }

            const response = await Method_POST_CONTEXTO("/contabil/lancamento/pesquisar", payload);

            const lancamentos = response?.lancamentos || [];
            const saldoAnterior = response?.saldo ? (response.saldo.creditos + response.saldo.debitos) : 0;

            setRawData({ lancamentos, saldoAnterior });
        } catch (error) {
            console.error("Erro ao buscar lançamentos:", error);
            message.error("Erro ao carregar dados do período.");
            setLoading(false);
        }
    };

    const applyFilters = () => {
        if (!rawData || !rawData.lancamentos) return;

        let lancamentosFiltrados = [...rawData.lancamentos];

        const categoriasMap = new Map();
        rawData.lancamentos.forEach(l => {
            if (l.categoria?.id) {
                categoriasMap.set(l.categoria.id, l.categoria.nome);
            }
        });
        setAvailableCategorias(
            Array.from(categoriasMap)
                .map(([id, nome]) => ({ id, nome }))
                .sort((a, b) => a.nome.localeCompare(b.nome))
        );

        if (filterTexto) {
            const term = filterTexto.toLowerCase();
            lancamentosFiltrados = lancamentosFiltrados.filter(l =>
                (l.descricao || '').toLowerCase().includes(term) ||
                String(l.valor || '').includes(term)
            );
        }

        if (filterPendencias) {
            lancamentosFiltrados = lancamentosFiltrados.filter(l => {
                const sId = l.situacao?.id || l.situacao;
                return sId === 'W' || sId === 'N';
            });
        }

        if (filterSituacao) {
            lancamentosFiltrados = lancamentosFiltrados.filter(l =>
                (l.situacao?.id || l.situacao) === filterSituacao
            );
        }

        if (filterTipo) {
            lancamentosFiltrados = lancamentosFiltrados.filter(l =>
                (l.tipo?.id || l.tipo) === filterTipo
            );
        }

        if (filterCategoria) {
            lancamentosFiltrados = lancamentosFiltrados.filter(l =>
                String(l.categoria?.id) === String(filterCategoria)
            );
        }

        // Filtro por CONTA agora é feito no BACKEND (handleSearch manda a conta
        // selecionada) — inclusive Caixa e contas "só banco". Aqui só ficam os
        // demais filtros (situação/categoria/tipo/texto).

        setData(lancamentosFiltrados);

        // Totais dos cards (Créditos/Débitos) calculados sobre o conjunto JÁ
        // FILTRADO — assim os cards seguem os mesmos filtros da tabela e do
        // dashboard (conta, situação, categoria, busca, tipo). Antes usavam o
        // bruto do período e divergiam quando havia filtro.
        const totais = lancamentosFiltrados.reduce((acc, l) => {
            const valor = Number(l.valor || 0);
            const tipoId = l.tipo?.id || l.tipo;
            if (tipoId === "C") acc.creditos += valor;
            else if (tipoId === "D") acc.debitos += valor;
            return acc;
        }, { creditos: 0, debitos: 0 });

        setStats({
            saldoAnterior: rawData.saldoAnterior || 0,
            creditosMes: totais.creditos,
            debitosMes: totais.debitos,
            saldoMes: (Number(rawData.saldoAnterior || 0) + totais.creditos - totais.debitos)
        });

        setLoading(false);
    };

    const handleNewRecebimento = () => {
        setModalConfig({ open: true, mode: 'new', type: 'C', data: null });
    };

    const handleNewPagamento = () => {
        setModalConfig({ open: true, mode: 'new', type: 'D', data: null });
    };

    const handleEdit = (record) => {
        setModalConfig({ open: true, mode: 'edit', type: record.tipo?.id || 'D', data: record });
    };

    const handleView = (record) => {
        setModalConfig({ open: true, mode: 'view', type: record.tipo?.id || 'D', data: record });
    };

    const handleSave = () => {
        handleSearch();
        setModalConfig(prev => ({ ...prev, open: false }));
    };

    const handleBulkReclassify = async (categoryId) => {
        if (selectedRowKeys.length === 0) return;

        try {
            message.loading({ content: `Reclassificando ${selectedRowKeys.length} lançamentos...`, key: 'bulk_update' });

            for (const id of selectedRowKeys) {
                // Busca o registro completo para manter os dados originais ao atualizar a categoria
                const record = rawData.lancamentos.find(l => l.id === id);
                if (!record) continue;

                const payload = {
                    id: record.id,
                    categoria: { id: categoryId },
                    tipo: { id: record.tipo?.id || record.tipo },
                    contaBancaria: { id: record.contaBancaria?.id || record.contaBancaria || "0" },
                    dataReferencia: record.dataReferencia,
                    descricao: record.descricao || "",
                    situacao: { id: record.situacao?.id || record.situacao },
                    valor: record.valor?.toString() || "0.00",
                };

                await Method_POST_CONTEXTO("/contabil/lancamento/atualizar", payload);
            }

            message.success({ content: `${selectedRowKeys.length} lançamentos reclassificados com sucesso!`, key: 'bulk_update' });
            setSelectedRowKeys([]);
            handleSearch();
            setReclassificacaoModalOpen(false);
        } catch (error) {
            console.error("Erro na reclassificação em lote:", error);
            message.error({ content: "Erro ao processar atualização em lote.", key: 'bulk_update' });
        }
    };

    const handleBulkStatus = async (statusId) => {
        if (selectedRowKeys.length === 0) return;

        try {
            message.loading({ content: `Atualizando status de ${selectedRowKeys.length} lançamentos...`, key: 'bulk_status' });

            for (const id of selectedRowKeys) {
                const record = rawData.lancamentos.find(l => l.id === id);
                if (!record) continue;

                const payload = {
                    id: record.id,
                    categoria: { id: record.categoria?.id || "0" },
                    tipo: { id: record.tipo?.id || record.tipo },
                    contaBancaria: { id: record.contaBancaria?.id || record.contaBancaria || "0" },
                    dataReferencia: record.dataReferencia,
                    descricao: record.descricao || "",
                    situacao: { id: statusId },
                    valor: record.valor?.toString() || "0.00",
                };

                await Method_POST_CONTEXTO("/contabil/lancamento/atualizar", payload);
            }

            message.success({ content: `${selectedRowKeys.length} lançamentos atualizados com sucesso!`, key: 'bulk_status' });
            setSelectedRowKeys([]);
            handleSearch();
        } catch (error) {
            console.error("Erro na atualização em lote:", error);
            message.error({ content: "Erro ao processar atualização em lote.", key: 'bulk_status' });
        }
    };

    const handleNewAccount = () => {
        setEditingAccount(null);
        setContaBancariaModalOpen(true);
    };

    const handleEditAccount = (account) => {
        setEditingAccount(account);
        setContaBancariaModalOpen(true);
    };

    const handleDelete = (record) => {
        Modal.confirm({
            title: 'Excluir Lançamento',
            content: `Deseja realmente excluir o lançamento "${record.descricao}"?`,
            okText: 'Sim, Excluir',
            okType: 'danger',
            cancelText: 'Cancelar',
            onOk: async () => {
                try {
                    // dataReferencia é obrigatória pro backend rotear: 2026+ remove no
                    // Pantheon; sem ela cai no banco legado e NÃO exclui de verdade.
                    await Method_POST_CONTEXTO("/contabil/lancamento/remover", { id: record.id, dataReferencia: record.dataReferencia });
                    message.success("Lançamento removido com sucesso!");
                    handleSearch();
                } catch (error) {
                    message.error("Erro ao remover lançamento.");
                }
            }
        });
    };

    const handleBulkDelete = () => {
        if (selectedRowKeys.length === 0) return;

        Modal.confirm({
            title: 'Excluir em Lote',
            content: `Deseja excluir os ${selectedRowKeys.length} lançamentos selecionados?`,
            okText: 'Sim, Excluir Todos',
            okType: 'danger',
            cancelText: 'Cancelar',
            onOk: async () => {
                try {
                    for (const id of selectedRowKeys) {
                        // dataReferencia do registro → backend roteia pro Pantheon (2026+).
                        const rec = data.find(d => String(d.id) === String(id));
                        await Method_POST_CONTEXTO("/contabil/lancamento/remover", { id, dataReferencia: rec?.dataReferencia });
                    }
                    message.success("Lançamentos removidos com sucesso!");
                    setSelectedRowKeys([]);
                    handleSearch();
                } catch (error) {
                    message.error("Erro ao remover alguns lançamentos.");
                    handleSearch();
                }
            }
        });
    };

    const handleDeleteAll = () => {
        if (data.length === 0) return;

        Modal.confirm({
            title: 'Excluir Toda a Movimentação Filtrada',
            content: `Atenção: Você está prestes a excluir ${data.length} lançamentos deste período. Esta ação é irreversível. Deseja continuar?`,
            okText: 'Sim, Excluir TUDO',
            okType: 'danger',
            cancelText: 'Cancelar',
            onOk: async () => {
                const hide = message.loading(`Removendo ${data.length} lançamentos...`, 0);
                try {
                    // Processe em série para evitar sobrecarga, mas com feedback
                    for (const item of data) {
                        await Method_POST_CONTEXTO("/contabil/lancamento/remover", { id: item.id, dataReferencia: item.dataReferencia });
                    }
                    message.success(`${data.length} lançamentos removidos com sucesso!`);
                    setSelectedRowKeys([]);
                    handleSearch();
                } catch (error) {
                    message.error("Erro ao processar a exclusão total.");
                    handleSearch();
                } finally {
                    hide();
                }
            }
        });
    };

    const handleDeleteDuplicates = () => {
        if (data.length < 2) {
            message.info("Não há lançamentos suficientes para verificar duplicados.");
            return;
        }

        const seen = new Map();
        const toDeleteIds = [];

        data.forEach(item => {
            const date = dayjs(item.dataReferencia).format('YYYY-MM-DD');
            const valor = String(item.valor || 0);
            const desc = (item.descricao || "").trim().toLowerCase();
            const tipo = typeof item.tipo === 'object' ? item.tipo?.id : item.tipo;
            const conta = typeof item.contaBancaria === 'object' ? item.contaBancaria?.id : item.contaBancaria;

            // Chave composta para identificar duplicidades exatas
            const key = `${date}|${valor}|${desc}|${tipo}|${conta}`;

            if (seen.has(key)) {
                // Guarda o registro inteiro: o remover precisa da dataReferencia
                // pro backend rotear pro Pantheon (2026+).
                toDeleteIds.push({ id: item.id, dataReferencia: item.dataReferencia });
            } else {
                seen.set(key, item.id);
            }
        });

        if (toDeleteIds.length === 0) {
            message.info("Nenhum lançamento duplicado encontrado no filtro atual.");
            return;
        }

        Modal.confirm({
            title: 'Remover Lançamentos Duplicados',
            icon: <InboxOutlined />,
            content: (
                <div>
                    <p>Foram identificados <strong>{toDeleteIds.length} lançamentos duplicados</strong>.</p>
                    <p>O sistema manterá apenas um registro original de cada e removerá as cópias idênticas (mesma data, valor, descrição e conta).</p>
                    <p style={{ color: '#e11d48', fontWeight: 'bold' }}>Deseja prosseguir com a limpeza?</p>
                </div>
            ),
            okText: 'Sim, Limpar Agora',
            okType: 'danger',
            cancelText: 'Não, Cancelar',
            onOk: async () => {
                const hide = message.loading(`Limpando ${toDeleteIds.length} duplicados...`, 0);
                try {
                    for (const del of toDeleteIds) {
                        await Method_POST_CONTEXTO("/contabil/lancamento/remover", { id: del.id, dataReferencia: del.dataReferencia });
                    }
                    message.success(`${toDeleteIds.length} duplicidades removidas com sucesso!`);
                    handleSearch();
                } catch (error) {
                    message.error("Erro ao tentar remover duplicados.");
                } finally {
                    hide();
                }
            }
        });
    };

    const handleRequestConfirmation = async (record) => {
        try {
            // dataReferencia junto: o backend usa pra NÃO mexer num registro errado
            // do banco legado quando o lançamento é 2026+ (id colide entre bancos).
            await Method_POST_CONTEXTO("/contabil/lancamento/confirmacao/solicitar", { id: record.id, dataReferencia: record.dataReferencia });
            message.success(`Confirmação solicitada para "${record.descricao}"`);
            handleSearch();
        } catch (error) {
            console.error("Erro ao solicitar confirmação:", error);
            message.error("Erro ao solicitar confirmação do cliente.");
        }
    };

    const handleMarkAsUnidentified = async (record) => {
        try {
            await Method_POST_CONTEXTO("/contabil/lancamento/marcar-nao-identificado", { id: record.id, dataReferencia: record.dataReferencia });
            message.success(`Lançamento "${record.descricao}" marcado como não identificado.`);
            handleSearch();
        } catch (error) {
            console.error("Erro ao marcar como não identificado:", error);
            message.error("Erro ao processar solicitação.");
        }
    };

    const [importModalConfig, setImportModalConfig] = useState({
        open: false,
        type: 'excel'
    });

    const handleImport = (type) => {
        setImportModalConfig({
            open: true,
            type: type
        });
    };

    const handleExport = async (type) => {
        if (type === 'extrato') {
            try {
                message.loading({ content: 'Gerando Extrato Excel Premium...', key: 'exporting' });

                const workbook = new ExcelJS.Workbook();
                const worksheet = workbook.addWorksheet("Extrato de Movimentações");

                const start = period[0].format('DD/MM/YYYY');
                const end = period[1].format('DD/MM/YYYY');

                // --- Título e Info do Relatório ---
                worksheet.mergeCells('A1:H1');
                const titleCell = worksheet.getCell('A1');
                titleCell.value = 'Extrato de Movimentações Financeiras';
                titleCell.font = { name: 'Segoe UI', size: 16, bold: true, color: { argb: 'FF0F172A' } };
                titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
                worksheet.getRow(1).height = 30;

                worksheet.mergeCells('A2:H2');
                const infoCell = worksheet.getCell('A2');
                infoCell.value = `Período: ${start} até ${end}`;
                infoCell.font = { name: 'Segoe UI', size: 11, italic: true, color: { argb: 'FF64748B' } };
                infoCell.alignment = { horizontal: 'center', vertical: 'middle' };
                worksheet.getRow(2).height = 20;

                // Linha vazia de separação
                worksheet.addRow([]);

                // --- Cabeçalhos da Tabela (Linha 4) ---
                const headerRowValues = ['Data', 'Situação', 'Categoria', 'Descrição', 'Tipo', 'Crédito', 'Débito', 'Conta / Banco'];
                const headerRow = worksheet.addRow(headerRowValues);

                headerRow.eachCell((cell) => {
                    cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
                    cell.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: 'FF2563EB' } // Premium Blue
                    };
                    cell.border = {
                        top: { style: 'thin' },
                        left: { style: 'thin' },
                        bottom: { style: 'thin' },
                        right: { style: 'thin' }
                    };
                    cell.alignment = { horizontal: 'center', vertical: 'middle' };
                });
                headerRow.height = 25;

                // Ajuste de colunas
                worksheet.columns = [
                    { key: 'data', width: 14 },
                    { key: 'situacao', width: 18 },
                    { key: 'categoria', width: 25 },
                    { key: 'descricao', width: 45 },
                    { key: 'tipo', width: 12 },
                    { key: 'credito', width: 18 },
                    { key: 'debito', width: 18 },
                    { key: 'banco', width: 30 }
                ];

                const situacaoMap = {
                    'A': 'Aberto',
                    'L': 'Liquidado',
                    'C': 'Cancelado',
                    'W': 'Aguardando',
                    'N': 'Não Identificado'
                };

                let totalCreditos = 0;
                let totalDebitos = 0;

                // --- Itens (Iniciando na linha 5) ---
                data.forEach((item, index) => {
                    const isCredito = item.tipo?.id === 'C' || item.categoria?.tipo?.id === 'C';
                    const valor = Number(item.valor || 0);

                    if (isCredito) totalCreditos += valor;
                    else totalDebitos += valor;

                    const sId = typeof item.situacao === 'object' ? item.situacao?.id : item.situacao;
                    const situacaoText = situacaoMap[sId] || 'Aberto';

                    let bancoDesc = 'N/A';
                    const cbId = typeof item.contaBancaria === 'object' ? item.contaBancaria?.id : item.contaBancaria;
                    if (cbId === '0' || String(cbId) === '0') {
                        bancoDesc = 'CAIXA';
                    } else {
                        const account = bankAccounts.find(acc => String(acc.id) === String(cbId));
                        if (account) {
                            bancoDesc = `${account.banco?.nome || 'Banco'} - ${account.conta}`;
                        } else if (item.contaBancaria?.banco?.nome) {
                            bancoDesc = `${item.contaBancaria.banco.nome} - ${item.contaBancaria.conta}`;
                        }
                    }

                    const row = worksheet.addRow([
                        dayjs(item.dataReferencia).format('DD/MM/YYYY'),
                        situacaoText,
                        item.categoria?.nome || 'Sem Categoria',
                        item.descricao || '',
                        isCredito ? 'Receita' : 'Despesa',
                        isCredito ? valor : 0,
                        !isCredito ? valor : 0,
                        bancoDesc
                    ]);

                    // Estilo de linha
                    row.eachCell((cell, colNumber) => {
                        cell.border = {
                            top: { style: 'thin', color: { argb: 'FFE2E8F0' } },
                            left: { style: 'thin', color: { argb: 'FFE2E8F0' } },
                            bottom: { style: 'thin', color: { argb: 'FFE2E8F0' } },
                            right: { style: 'thin', color: { argb: 'FFE2E8F0' } }
                        };

                        if (index % 2 === 0) {
                            cell.fill = {
                                type: 'pattern',
                                pattern: 'solid',
                                fgColor: { argb: 'FFF8FAFC' }
                            };
                        }

                        // Formatação específica por coluna
                        if (colNumber === 6 || colNumber === 7) {
                            cell.numFmt = '"R$ "#,##0.00';
                            if (cell.value === 0) {
                                cell.font = { color: { argb: 'FFCBD5E1' } }; // Cinza claro para zeros
                            } else if (colNumber === 6) {
                                cell.font = { color: { argb: 'FF059669' }, bold: true };
                            } else {
                                cell.font = { color: { argb: 'FFE11D48' }, bold: true };
                            }
                        }
                    });
                });

                // --- Linha de Totais ---
                worksheet.addRow([]); // Espaçador
                const footerRow = worksheet.addRow([
                    'TOTAL DO PERÍODO', '', '', '', '',
                    totalCreditos,
                    totalDebitos,
                    ''
                ]);

                footerRow.getCell(1).font = { bold: true, size: 12 };
                worksheet.mergeCells(footerRow.number, 1, footerRow.number, 5);
                footerRow.getCell(1).alignment = { horizontal: 'right' };

                const cCell = footerRow.getCell(6);
                cCell.numFmt = '"R$ "#,##0.00';
                cCell.font = { bold: true, color: { argb: 'FF059669' }, size: 12 };

                const dCell = footerRow.getCell(7);
                dCell.numFmt = '"R$ "#,##0.00';
                dCell.font = { bold: true, color: { argb: 'FFE11D48' }, size: 12 };

                // Linha de Saldo Final
                const balanceRow = worksheet.addRow([
                    'SALDO RESULTANTE', '', '', '', '',
                    totalCreditos - totalDebitos,
                    '',
                    ''
                ]);
                worksheet.mergeCells(balanceRow.number, 1, balanceRow.number, 5);
                balanceRow.getCell(1).font = { bold: true, size: 12 };
                balanceRow.getCell(1).alignment = { horizontal: 'right' };

                const bCell = balanceRow.getCell(6);
                bCell.numFmt = '"R$ "#,##0.00';
                bCell.font = { bold: true, size: 13 };
                const saldo = totalCreditos - totalDebitos;
                bCell.font.color = { argb: saldo >= 0 ? 'FF059669' : 'FFE11D48' };

                // --- Funcionalidades Extras ---
                worksheet.autoFilter = 'A4:H4'; // Filtros automáticos no cabeçalho
                worksheet.views = [
                    { state: 'frozen', ySplit: 4 } // Congelar as primeiras 4 linhas
                ];

                const buffer = await workbook.xlsx.writeBuffer();
                saveAs(new Blob([buffer]), `extrato_financeiro_${start.replace(/\//g, '-')}.xlsx`);

                message.success({ content: 'Extrato gerado com sucesso!', key: 'exporting' });
                return;
            } catch (error) {
                console.error('Erro ao gerar Excel:', error);
                message.error({ content: 'Erro ao gerar Extrato Excel.', key: 'exporting' });
                return;
            }
        }

        if (type === 'excel-dominio') {
            try {
                message.loading({ content: 'Preparando Layout Domínio...', key: 'exporting' });

                const workbook = new ExcelJS.Workbook();
                const worksheet = workbook.addWorksheet("Dominio_Contabilidade");

                // Cabeçalho simplificado como na imagem
                const headerRow = worksheet.addRow(['Período', 'Data', 'Valor (R$)', '', '', 'Descrição']);
                headerRow.eachCell((cell) => {
                    cell.font = { bold: true };
                    cell.border = { bottom: { style: 'thin' } };
                });

                // Ajuste de colunas
                worksheet.columns = [
                    { key: 'periodo', width: 12 },
                    { key: 'data', width: 12 },
                    { key: 'valor', width: 15 },
                    { key: 'debito', width: 10 },
                    { key: 'credito', width: 10 },
                    { key: 'descricao', width: 80 }
                ];

                const startPeriodStr = period[0].format('DD-MM-YYYY');

                data.forEach((item) => {
                    const isCredito = item.tipo?.id === 'C' || item.categoria?.tipo?.id === 'C';
                    const valor = Number(item.valor || 0);

                    // Busca o código contábil da categoria (Prioridade para dominioId)
                    const catCode = item.categoria?.dominioId || 
                                    item.categoria?.contaContabil?.codigoReduzido || 
                                    item.categoria?.id || 
                                    (isCredito ? '10000' : '612');

                    // Descobre o código da conta bancária/caixa (Prioridade para dominioId)
                    let bankCode = '5000'; 
                    const cbId = typeof item.contaBancaria === 'object' ? item.contaBancaria?.id : item.contaBancaria;
                    
                    if (cbId === '0' || String(cbId) === '0') {
                        bankCode = '1'; 
                    } else {
                        const account = bankAccounts.find(acc => String(acc.id) === String(cbId));
                        if (account) {
                            bankCode = account.dominioId || account.contaContabil?.codigoReduzido || '5000';
                        }
                    }

                    worksheet.addRow([
                        startPeriodStr,
                        dayjs(item.dataReferencia).format('DD-MM-YYYY'),
                        valor,
                        isCredito ? bankCode : catCode, // Débito
                        isCredito ? catCode : bankCode, // Crédito
                        item.descricao || ''
                    ]);
                });

                // Formatação de valor na coluna C
                worksheet.getColumn(3).numFmt = '"R$ "#,##0.00';

                const buffer = await workbook.xlsx.writeBuffer();
                saveAs(new Blob([buffer]), `dominio_contabil_${startPeriodStr}.xlsx`);

                message.success({ content: 'Layout Domínio gerado com sucesso!', key: 'exporting' });
                return;
            } catch (error) {
                console.error('Erro ao gerar Excel Domínio:', error);
                message.error({ content: 'Erro ao gerar Layout Domínio.', key: 'exporting' });
                return;
            }
        }

        try {
            message.loading({ content: `Gerando arquivo ${type.toUpperCase()}...`, key: 'exporting' });

            const start = period[0].format('YYYY-MM-DD');
            const end = period[1].format('YYYY-MM-DD');

            const url = `/contabil/lancamento/export/${type}?dataInicio=${start}&dataTermino=${end}`;

            const response = await Method_GET_CONTEXTO_PDF(url);

            if (response.data) {
                const blob = new Blob([response.data], {
                    type: response.headers['content-type'] || 'application/octet-stream'
                });
                const downloadUrl = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = downloadUrl;

                const extension = type === 'excel' ? 'xlsx' : type;
                link.setAttribute('download', `lancamentos_${start}_a_${end}.${extension}`);

                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(downloadUrl);

                message.success({ content: `Arquivo ${type.toUpperCase()} gerado com sucesso!`, key: 'exporting' });
            }
        } catch (error) {
            console.error(`Erro ao exportar ${type}:`, error);
            message.error({ content: `Erro ao gerar arquivo ${type.toUpperCase()}.`, key: 'exporting' });
        }
    };

    return (
        <ConfigProvider locale={ptBR}>
            <AnimatePresence>
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="movimentacao-financeira-container"
                >
                    <div className="movimentacao-financeira-content">
                        <MovimentacaoFinanceiraHeader
                            period={period}
                            setPeriod={setPeriod}
                            bankAccount={bankAccount}
                            setBankAccount={setBankAccount}
                            bankAccounts={contasAgrupadas}
                            onSearch={handleSearch}
                            onNewRecebimento={handleNewRecebimento}
                            onNewPagamento={handleNewPagamento}
                            onImport={handleImport}
                            onExport={handleExport}
                            filterPendencias={filterPendencias}
                            setFilterPendencias={setFilterPendencias}
                            hasPendencias={hasPendencias}
                            filterSituacao={filterSituacao}
                            setFilterSituacao={setFilterSituacao}
                            filterTipo={filterTipo}
                            setFilterTipo={setFilterTipo}
                            filterTexto={filterTexto}
                            setFilterTexto={setFilterTexto}
                            filterCategoria={filterCategoria}
                            setFilterCategoria={setFilterCategoria}
                            availableCategorias={availableCategorias}
                            onReclassificar={() => setReclassificacaoModalOpen(true)}
                            onShowInstrucoes={() => setInstrucoesModalOpen(true)}
                            onShowDashboard={() => setDashboardModalOpen(true)}
                            onShowRules={() => setRulesModalOpen(true)}
                            onShowWhatsNew={handleOpenWhatsNew}
                            whatsNewBadge={whatsNewBadge}
                            onOpenFinance={() => setOpenFinanceModalOpen(true)}
                            onOpenFinanceAviso={() => setOpenFinanceAvisoOpen(true)}
                            onNewAccount={handleNewAccount}
                            onManageAccounts={() => setGerenciarContasModalOpen(true)}
                            isContador={isContador}
                            selectedCount={selectedRowKeys.length}
                        />

                        <MovimentacaoFinanceiraStats 
                            stats={stats} 
                            loading={loading} 
                            pendenciesCount={globalPendencies.length}
                        />

                        <MovimentacaoFinanceiraTable
                            data={data}
                            loading={loading}
                            onEdit={handleEdit}
                            onDelete={handleDelete}
                            onView={handleView}
                            selectedRowKeys={selectedRowKeys}
                            setSelectedRowKeys={setSelectedRowKeys}
                            onBulkDelete={handleBulkDelete}
                            onDeleteAll={handleDeleteAll}
                            onDeleteDuplicates={handleDeleteDuplicates}
                            isContador={isContador}
                            onRequestConfirmation={handleRequestConfirmation}
                            onMarkAsUnidentified={handleMarkAsUnidentified}
                            globalPendencies={globalPendencies}
                            bankAccounts={bankAccounts}
                        />

                        <LancamentoModal
                            open={modalConfig.open}
                            mode={modalConfig.mode}
                            type={modalConfig.type}
                            initialData={modalConfig.data}
                            bankAccounts={bankAccounts}
                            onCancel={() => setModalConfig(prev => ({ ...prev, open: false }))}
                            onSave={handleSave}
                            isContador={isContador}
                        />

                        <ImportarArquivoModal
                            open={importModalConfig.open}
                            tipoArquivo={importModalConfig.type}
                            onClose={() => setImportModalConfig(prev => ({ ...prev, open: false }))}
                            onShowInstructions={() => {
                                setImportModalConfig(prev => ({ ...prev, open: false }));
                                setInstrucoesModalOpen(true);
                            }}
                        />

                        <InstrucoesModal
                            open={instrucoesModalOpen}
                            onClose={() => setInstrucoesModalOpen(false)}
                        />

                        <ReclassificacaoModal
                            open={reclassificacaoModalOpen}
                            onClose={() => setReclassificacaoModalOpen(false)}
                            onConfirm={handleBulkReclassify}
                            batchMode={selectedRowKeys.length > 0}
                        />

                        <ContaBancariaModal
                            open={contaBancariaModalOpen}
                            onClose={() => {
                                setContaBancariaModalOpen(false);
                                setEditingAccount(null);
                            }}
                            onSave={fetchAccounts}
                            initialData={editingAccount}
                        />

                        <GerenciarContasModal
                            open={gerenciarContasModalOpen}
                            onClose={() => setGerenciarContasModalOpen(false)}
                            onNewAccount={handleNewAccount}
                            onEditAccount={handleEditAccount}
                            onUpdate={fetchAccounts}
                        />

                        <DashboardCustosModal
                            open={dashboardModalOpen}
                            onClose={() => setDashboardModalOpen(false)}
                            data={data}
                        />

                        <WhatsNewModal
                            open={whatsNewModalOpen}
                            onClose={() => setWhatsNewModalOpen(false)}
                        />

                        <OpenFinanceModal
                            open={openFinanceModalOpen}
                            onClose={() => setOpenFinanceModalOpen(false)}
                        />

                        <OpenFinanceAvisoModal
                            open={openFinanceAvisoOpen}
                            onClose={() => setOpenFinanceAvisoOpen(false)}
                        />

                        <RegrasClassificacaoModal
                            open={rulesModalOpen}
                            onClose={() => setRulesModalOpen(false)}
                            onApplyRules={handleSearch}
                            data={rawData.lancamentos || []}
                            periodoFiltro={period}
                            isContador={isContador}
                        />
                    </div>
                </motion.div>

                <AnimatePresence>
                    {selectedRowKeys.length > 0 && isContador && (
                        <BatchActionsBar 
                            selectedCount={selectedRowKeys.length}
                            onClear={() => setSelectedRowKeys([])}
                            onBulkDelete={handleBulkDelete}
                            onBulkCategory={() => setReclassificacaoModalOpen(true)}
                            onBulkStatus={handleBulkStatus}
                            onBulkAccount={() => message.info("Selecione o novo banco no modal de edição em massa.")}
                        />
                    )}
                </AnimatePresence>
            </AnimatePresence>
        </ConfigProvider>
    );
}

export default MovimentacaoFinanceira;